/**
 * Login modal component with multiple authentication options.
 *
 * Provides UI for Google, Facebook, and email/password authentication.
 */

import React, { useState, useEffect } from 'react';

import { useAuth } from '../../hooks/useAuth';
import { useTranslation } from '../../hooks/useTranslation';
import { handleError, isUserCancelledAuth } from '../../utils/errorHandler';
import { SocialLoginButton } from './SocialLoginButton';
import { WalletLoginButton } from './WalletLoginButton';
import { WALLET_LOGIN_ENABLED } from '../../constants/features';

/**
 * Props for LoginModal component.
 */
interface LoginModalProps {
  /** Whether modal is open */
  isOpen: boolean;
  /** Close modal callback */
  onClose: () => void;
  /** Optional custom message to display (e.g., "Sign in to generate videos") */
  message?: string | null;
}

/**
 * Login modal component.
 *
 * @param props - Component props
 * @returns LoginModal component
 */
export const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose, message }) => {
  const { t } = useTranslation();
  const {
    signInWithGoogle,
    signInWithEmail,
    signUpWithEmail,
    signInWithWallet,
    loading,
    isWeb3AuthReady,
  } = useAuth();

  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Clear any stale error whenever the modal is closed so it doesn't linger
  // when reopened. The component stays mounted (returns null below) while closed,
  // so local state survives without this reset.
  useEffect(() => {
    if (!isOpen) setError(null);
  }, [isOpen]);

  if (!isOpen) return null;

  /**
   * Handle Google sign-in.
   * On user cancellation we show a friendly message instead of leaking the raw
   * provider error (e.g. "OAuth error: access_denied"); genuine failures fall
   * back to a generic localized message. The raw error is still logged for devs.
   */
  const handleGoogleSignIn = async (): Promise<void> => {
    try {
      setError(null);
      await signInWithGoogle();
      onClose();
    } catch (err) {
      handleError(err, 'Login'); // log raw error for debugging
      setError(
        t(
          isUserCancelledAuth(err)
            ? 'auth.errors.signInCancelled'
            : 'auth.errors.googleSignInFailed'
        )
      );
    }
  };

  /**
   * Handle Web3Auth wallet sign-in.
   */
  const handleWalletSignIn = async (): Promise<void> => {
    try {
      setError(null);
      await signInWithWallet();
      onClose();
    } catch (err) {
      handleError(err, 'Login'); // log raw error for debugging
      setError(
        t(
          isUserCancelledAuth(err)
            ? 'auth.errors.signInCancelled'
            : 'auth.errors.walletSignInFailed'
        )
      );
    }
  };

  /**
   * Handle email/password form submission.
   */
  const handleEmailSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    try {
      setError(null);

      if (isSignUp) {
        await signUpWithEmail(email, password, displayName || undefined);
      } else {
        await signInWithEmail(email, password);
      }

      onClose();
    } catch (err) {
      setError(handleError(err, 'Login', t('auth.errors.emailAuthFailed')));
    }
  };

  /**
   * Toggle between sign-in and sign-up modes.
   */
  const toggleMode = (): void => {
    setIsSignUp(!isSignUp);
    setError(null);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-2 xs:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="login-modal-title"
    >
      <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-xl w-full max-w-[calc(100vw-1rem)] xs:max-w-sm sm:max-w-md p-4 xs:p-5 sm:p-6 relative max-h-[calc(100vh-1rem)] overflow-y-auto scroll-smooth-touch">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 active:text-gray-800 touch-target"
          type="button"
          aria-label="Close modal"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {/* Title */}
        <h2
          id="login-modal-title"
          className="text-xl xs:text-2xl font-bold text-gray-900 dark:text-white mb-2 pr-8"
        >
          {isSignUp ? t('auth.signUp') : t('auth.signIn')}
        </h2>

        {/* Custom message (e.g., "Sign in to generate videos") */}
        {message && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 xs:mb-6">{message}</p>
        )}
        {!message && <div className="mb-3 xs:mb-4" />}

        {/* Error message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Social login buttons */}
        <div className="space-y-2 xs:space-y-3 mb-4 xs:mb-6">
          <SocialLoginButton provider="google" onClick={handleGoogleSignIn} disabled={loading}>
            {t('auth.continueWithGoogle')}
          </SocialLoginButton>

          {WALLET_LOGIN_ENABLED && (
            <WalletLoginButton
              onClick={handleWalletSignIn}
              disabled={loading || !isWeb3AuthReady}
              loading={loading}
            >
              {t('auth.continueWithWallet')}
            </WalletLoginButton>
          )}
        </div>

        {/* Divider */}
        <div className="relative mb-4 xs:mb-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300 dark:border-gray-600" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white dark:bg-secondary-800 text-gray-500 dark:text-gray-400">
              {t('auth.orContinueWith')}
            </span>
          </div>
        </div>

        {/* Email/password form */}
        <form onSubmit={handleEmailSubmit} className="space-y-3 xs:space-y-4">
          {isSignUp && (
            <div>
              <label htmlFor="displayName" className="label-gray-sm">
                {t('auth.displayName')}
              </label>
              <input
                id="displayName"
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="w-full px-3 py-2.5 text-base sm:text-sm border border-gray-300 dark:border-gray-600 dark:bg-secondary-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('auth.displayNamePlaceholder')}
              />
            </div>
          )}

          <div>
            <label htmlFor="email" className="label-gray-sm">
              {t('auth.email')}
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-3 py-2.5 text-base sm:text-sm border border-gray-300 dark:border-gray-600 dark:bg-secondary-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t('auth.emailPlaceholder')}
            />
          </div>

          <div>
            <label htmlFor="password" className="label-gray-sm">
              {t('auth.password')}
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              className="w-full px-3 py-2.5 text-base sm:text-sm border border-gray-300 dark:border-gray-600 dark:bg-secondary-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t('auth.passwordPlaceholder')}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 xs:py-2 px-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:bg-blue-300 transition-colors duration-200 text-base xs:text-sm"
          >
            {isSignUp ? t('auth.signUp') : t('auth.signIn')}
          </button>
        </form>

        {/* Toggle mode */}
        <div className="mt-4 xs:mt-6 text-center">
          <button
            onClick={toggleMode}
            className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
            type="button"
          >
            {isSignUp ? t('auth.alreadyHaveAccount') : t('auth.needAccount')}
          </button>
        </div>
      </div>
    </div>
  );
};
